import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Search, Lightbulb, Rocket, CheckCircle } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: <Search className="w-6 h-6" />,
    title: "Descubrimiento",
    description:
      "Analizamos tu marca, objetivos y audiencia para crear una estrategia personalizada.",
  },
  {
    number: "02",
    icon: <Lightbulb className="w-6 h-6" />,
    title: "Propuesta Creativa",
    description:
      "Desarrollamos conceptos creativos con timeline, entregables y presupuesto claros.",
  },
  {
    number: "03",
    icon: <Rocket className="w-6 h-6" />,
    title: "Desarrollo",
    description:
      "Ejecutamos el proyecto con los más altos estándares de calidad.",
  },
  {
    number: "04",
    icon: <CheckCircle className="w-6 h-6" />,
    title: "Entrega",
    description:
      "Entregamos el proyecto final y ofrecemos soporte para optimizar resultados.",
  },
];

export default function ProcessSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section
      id="process"
      ref={ref}
      className="py-24 lg:py-32 bg-[#050505] relative overflow-hidden"
      data-testid="process-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 lg:mb-20"
        >
          <p className="overline mb-4">Nuestro Proceso</p>
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-white mb-6">
            Cómo{" "}
            <span className="italic text-[#D4AF37]">trabajamos</span>
          </h2>
          <p className="text-[#A3A3A3] max-w-2xl mx-auto text-sm lg:text-base">
            Un proceso claro y eficiente que garantiza resultados excepcionales.
          </p>
        </motion.div>

        {/* Process Steps - Mobile: Vertical, Desktop: Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="relative bg-[#0a0a0a] p-6 rounded-lg border border-white/5"
              data-testid={`process-step-${index}`}
            >
              {/* Step Number */}
              <span className="absolute top-4 right-4 font-playfair text-4xl lg:text-5xl font-bold text-white/5">
                {step.number}
              </span>

              {/* Icon */}
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center text-[#D4AF37] mb-4">
                {step.icon}
              </div>

              {/* Title */}
              <h3 className="font-playfair text-lg lg:text-xl font-bold text-white mb-2">
                {step.title}
              </h3>

              {/* Description */}
              <p className="text-[#A3A3A3] text-sm leading-relaxed">
                {step.description}
              </p>

              {/* Connector Line - Desktop only */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-[#D4AF37]/20" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
